import { useCallback, useEffect, useMemo } from 'react';
import { Icon, Select } from '@trussworks/react-uswds';
import type { AuthorizedUser, AuthorizedUserPermission } from '@api/services/authorized-user/types';
import InternalLink from '@components/internal-link/InternalLink';
import Table from '@components/table/Table';
import { useTableStore } from '@stores';
import dayjs from '@src/utils/dayjs';
import { ProfileRoles, UserPermissions } from '@src/utils/enums';
import { authorizedUsersTableConfig, type AuthorizedUsersTableVariant } from './AuthorizedUsersTable.config';
import './AuthorizedUsersTable.scss';

type AuthorizedUsersTableProps = {
  users: AuthorizedUser[];
  variant?: AuthorizedUsersTableVariant;
  onRemoveUser?: (user: AuthorizedUser) => void;
  onPermissionsChange?: (user: AuthorizedUser, selectedValue: string) => void;
  userIssueMap?: Record<string, string>;
  ownershipEligibleUserIds?: Set<number>;
};

const formatDate = (value?: string | null): string => (value ? dayjs(value).format('MM/DD/YYYY') : '—');

const formatPermission = (permission: AuthorizedUserPermission): string => {
  switch (permission) {
    case UserPermissions.Owner:
      return 'Project Owner';
    case UserPermissions.ReadWrite:
      return 'Read/Write';
    case UserPermissions.ReadOnly:
      return 'Read Only';
    default:
      return permission;
  }
};

const AuthorizedUsersTable = ({
  users,
  variant = 'current',
  onRemoveUser,
  onPermissionsChange,
  userIssueMap = {},
  ownershipEligibleUserIds,
}: AuthorizedUsersTableProps) => {
  const isHistory = variant === 'history';
  const config = authorizedUsersTableConfig[variant];
  const { tableId } = config;

  const canReceiveOwnership = useCallback(
    (user: AuthorizedUser) => ownershipEligibleUserIds?.has(user.authorizedUserID) ?? true,
    [ownershipEligibleUserIds]
  ); //@TODO TEMPORARY, REMOVE THIS IN RRS-3053

  const hasStatusColumn = useMemo(() => {
    if (isHistory) return false;

    return users.some((user) => {
      const issueKey = user.authorizedUserID !== 0 ? 'existing_' + user.authorizedUserID : 'new_' + user.userID;

      return Boolean(userIssueMap[issueKey]);
    });
  }, [isHistory, users, userIssueMap]);

  const headerData = useMemo(() => {
    const baseHeader = (config.headerData?.[0] ?? []).filter(
      (column) => column.name !== 'status' && column.name !== 'statusSort'
    );

    if (!hasStatusColumn) {
      return [baseHeader];
    }
    const adjustedHeader = baseHeader.map((column) => ({
      ...column,
      width: config.headerWidthsWithStatus?.[column.name] ?? column.width,
    }));

    return [
      [
        {
          label: 'Status',
          name: 'statusSort',
          isSortable: false,
          width: config.statusColumnWidth ?? '30%',
        },
        ...adjustedHeader,
      ],
    ];
  }, [config.headerData, config.headerWidthsWithStatus, config.statusColumnWidth, hasStatusColumn]);

  const columnOrder = useMemo(() => {
    const baseOrder = config.columnOrder.filter((column) => column.name !== 'status');

    if (!hasStatusColumn) return baseOrder;

    return [{ name: 'status', alignment: 'center' as const }, ...baseOrder];
  }, [config.columnOrder, hasStatusColumn]);

  const { setPageNumber, setPageSize, setSortColumn, setSortDirection } = useTableStore();

  useEffect(() => {
    setPageSize(tableId, 15);
    setSortColumn(tableId, config.defaultSortColumn);
    setSortDirection(tableId, 'desc');
  }, [config.defaultSortColumn, setPageSize, setSortColumn, setSortDirection, tableId]);

  useEffect(() => {
    setPageNumber(tableId, 1);
  }, [setPageNumber, tableId, users.length]);

  const rowData = useMemo(
    () =>
      users.map((user) => {
        const fullName = `${user.firstName} ${user.lastName}`;
        const permission = formatPermission(user.permission);
        const isRegulator = user.role === ProfileRoles.USACERegulator;
        const selectId = `user-role-${user.authorizedUserID}`;
        const issueKey = user.authorizedUserID !== 0 ? 'existing_' + user.authorizedUserID : 'new_' + user.userID;
        const issueMessage = userIssueMap[issueKey];

        return {
          id: user.authorizedUserID !== 0 ? user.authorizedUserID : user.userID,
          statusSort: issueMessage ? 0 : 1,
          status: issueMessage ? (
            <div className='display-flex flex-align-center flex-gap-05'>
              <Icon.Warning size={3} className='text-warning-dark' aria-label='Warning' role='img' />
              <span className='text-bold'>{issueMessage.match(/\[([^\]]+)\]/)?.[1] ?? 'WARNING'}</span>
            </div>
          ) : (
            <span aria-hidden='true'>-</span>
          ),
          userSort: `${user.lastName.trim().toLowerCase()} ${user.firstName.trim().toLowerCase()}`,
          roleSort: user.role.toLowerCase(),
          emailSort: user.email.trim().toLowerCase(),
          accessLevelSort: permission.toLowerCase(),
          accessGrantedSort: user.beginValid ?? '',
          accessEndedSort: user.endValid ?? '',
          user: (
            <div className='authorized-users-table__user'>
              <strong>{fullName}</strong>
            </div>
          ),
          role: user.role,
          email: (
            <div className='authorized-users-table__email'>
              <InternalLink content={user.email} title={`Email ${user.email}`} href={`mailto:${user.email}`} />
            </div>
          ),
          accessLevel: isHistory ? (
            permission
          ) : (
            <Select
              id={selectId}
              name={selectId}
              aria-label={`Access level for ${fullName}`}
              value={user.permission}
              disabled={isRegulator}
              title={isRegulator ? 'Access levels cannot be changed for USACE Regulators' : undefined}
              onChange={(event) => onPermissionsChange?.(user, event.target.value)}
            >
              {canReceiveOwnership(user) && <option value={UserPermissions.Owner}>Project Owner</option>}
              <option value={UserPermissions.ReadWrite}>Read/Write</option>
              <option value={UserPermissions.ReadOnly}>Read Only</option>
            </Select>
          ),
          accessGranted: formatDate(user.beginValid),
          accessEnded: formatDate(user.endValid),
          actions: isHistory ? null : (
            <button
              type='button'
              className='authorized-users-table__delete'
              aria-label={
                isRegulator
                  ? `${fullName} cannot be removed because they are a USACE Regulator`
                  : `Remove ${fullName} from this request`
              }
              title={isRegulator ? 'USACE Regulators cannot be removed' : `Remove ${fullName}`}
              disabled={isRegulator}
              onClick={() => onRemoveUser?.(user)}
            >
              <Icon.Delete className='authorized-users-table__delete-icon' aria-hidden='true' />
            </button>
          ),
        };
      }),
    [isHistory, onPermissionsChange, onRemoveUser, users, userIssueMap, canReceiveOwnership]
  );

  return (
    <Table
      id={tableId}
      className={
        hasStatusColumn ? 'authorized-users-table authorized-users-table--with-status' : 'authorized-users-table'
      }
      headerData={headerData}
      columnOrder={columnOrder}
      rowData={rowData}
      pageSizeOptions={[5, 10, 15, 20, 25, 50]}
      showPageSizeSelector
      noRecordsText={config.noRecordsText}
    />
  );
};

export default AuthorizedUsersTable;
